import { DataType } from 'surveys/common/types/DaoCommon';

export type OptionID = string;
export type PageID = string;
export type QuestionID = string;
export type QuestionExternalID = string;
export type AnswerID = string;
export type AnswerExternalID = string;
export type ThemeID = string;
export type SurveyTemplateID = string;
export type SurveyID = string;
export type SurveyExternalID = string;
export type ReviewRequestID = string;
export type IsLastPage = boolean;
export type LocationCode = string;
export type ViewID = string;
export type ViewColumnID = string;
export type SecondaryFilterID = string;
export type PropertyID = string;
export type RoleID = string;

export enum ValidationType {
    Required = 0,
    Number = 1,
    Date = 2,
    Email = 3,
    Length = 4,
    Phone = 5,
    FileType = 6,
}

export enum ResourceType {
    Image = 'image',
    Video = 'video',
    Audio = 'audio',
    Text = 'text',
}

export interface Media {
    id?: any | string | null;
    title: string | null;
    url: string | null;
    mediaType?: any | null;
    mediaMetadata: MediaMetadata | null;
}

export interface MediaMetadata {
    id: string | null;
    url: string | null;
    format: string | null;
    bytes: number | null;
    resourceType: ResourceType;
    imageMetadata?: ImageMetadata | null;
    audioMetadata?: AudioMetadata | null;
    videoMetadata?: VideoMetadata | null;
}
export interface ImageMetadata {
    width: number;
    height: number;
    pages: number;
}
export interface AudioMetadata {
    codec: string;
    channels: number;
    bitRate: number;
    frequency: number;
    channelLayout: string;
    profile: string;
    codecLongName: string;
}
export interface VideoMetadata {
    codec: string;
    bitRate: number;
    dar: string;
    level: number;
    pixelFormat: string;
    profile: string;
    duration: number;
    fameRate: number;
    frameCount: number;
    codecLongName: string;
}
export interface Answer {
    answerID: AnswerID;
    answerValues: string[];
    shownOptions: string[] | null;
    dataType: DataType | null;
    medias?: Media[] | null;
}

export enum SurveyTemplateProperty {
    ShowQuestionNumbers = 0,
    ShowProgressBar = 1,
    ShowAsteriskForRequired = 2,
    DefaultLocale = 3,
    MaxInactivityTime = 4,
    Logo = 5,
    DeviceOptimized = 6,
    Kiosk = 7,
    ReviewRequestExpirationDays = 8,
    RequestLinkUpdatesNotAllowed = 9,
    SavePartialSurveyResultAllowed = 10,
    Anonymize = 11,
    LetSurveyTakerChangeLocale = 12,
    HighQualityResultsOnly = 13,
    SolicitedInvitesOnly = 14,
    IgnoreHiddenQuestionAnswers = 15,
}

export enum SubQuestionType {
    Comment = 0,
    Dropdown = 1,
    Heading = 2,
    Label = 3,
    NPS = 4,
    Rating = 5,
    ThankYou = 6,
    TermsOfService = 7,
    Checkboxes = 8,
    RadioButtons = 9,
    Date = 10,
    Image = 11,
    Name = 12,
    Email = 13,
    Phone = 14,
    HTML = 15,
    FileUpload = 16,
    VideoUpload = 17,
    Redirect = 18,
    Anonymous = 19,
    VideoUploadVPM = 20,
    ItemFeedback = 21,
    RankOrder = 22,
}

export enum TemplateStatus {
    Active = 0,
    Design = 1,
    Test = 2,
    Closed = 3,
    Internal = 4,
}

export enum Suspect {
    ShortCompletionTime = 0,
    StraightLiningGridQuestions = 1,
    ShortOverallComment = 2,
    SkippingNonRequiredQuestions = 3,
    UserMarked = 4,
    DuplicateIPAddress = 5,
}

export enum Visibility {
    Shown = 0,
    Hidden = 1,
    ConditionallyHidden = 2,
    ConditionallyShown = 3,
}

export interface VisibilityConfig {
    visibility: Visibility;
    visibilityConfigScript?: string | null;
    rule?: any; // TODO: R4eRules.Rule
}

export interface OverallRatingQIDs {
    ratingQID: string | null;
    computedRatingQID?: string | null;
}

export interface StandardWidget {
    blockNPStoRating?: boolean;
    commentQIDs?: QuestionID[] | null;
    customTopBoxTitle?: string | null;
    hideRating?: boolean;
    hideSurveyTemplateName?: boolean;
    computedNpsQID: string | null;
    npsQID: QuestionID | null;
    npsQuestionLabel?: string | null;
    overallRatingQIDs: OverallRatingQIDs | null;
    reviewerNameQID?: QuestionID | null;
    reviewerEmailQID?: QuestionID | null;
    reviewerPhoneQID?: QuestionID | null;
    topBoxQID?: QuestionID | null;
    topTwoBoxQID?: QuestionID | null;
}

export interface RoleConfiguration {
    restrictedQuestionIds: { [key: string]: string[] } | null;
    restrictedPropertyIds: { [key: string]: string[] } | null;
}

export enum Filterable {
    Primary = 0,
    Secondary = 1,
    None = 2,
}

export enum TemplateVariableType {
    Date = 0,
    Normal = 1,
}

export interface TemplateVariable {
    name: string;
    label: string;
    filterable: Filterable;
    type: TemplateVariableType;
    isPII: boolean;
}

export interface Banner {
    texts: { [key: string]: string };
    logo: string;
    disabled: boolean;
}

export enum SubQuestionGroup {
    Matrix = 0,
    NonMatrix = 1,
    Other = 2,
    ItemFeedback = 3,
}

export enum WidgetPropertyTypes {
    TextNumberOfLines = 0,
    ChoicesOrientation = 1,
    LinkValue = 2,
    LinkStart = 3,
    LinkEnd = 4,
    IsSentiment = 5,
    IsCheckToAnonymize = 6,
    VPMProjectId = 7,
    VPMProjectIdent = 8,
    IsFocusOnSearch = 9,
    IsStrengthMeterEnabled = 10,
}

export enum ValidationProperty {
    Min = 'min',
    Max = 'max',
    FileType = 'file-type',
    CustomFileTypes = 'custom-file-types',
    Future = 'future',
}

export enum PropertyValues {
    True = 'true',
    False = 'false',
    ChoicesOrientationHorizontal = 'horizontal',
    ChoicesOrientationScale = 'scale',
    ChoicesOrientationVertical = 'vertical',
}

export enum DeviceType {
    Desktop = 0,
    Mobile = 1,
    Tablet = 2,
    Platform = 3,
}

export interface ConversationalResponse {
    rule: any; // TODO: R4ERules.Rule
    conversationalResponseScript: string | null;
    messages: { [key: string]: string } | null;
}

export enum TemplateType {
    Normal = 0,
    Conversational = 1,
    Kiosk = 2,
}

export enum Survey3Metric {
    Bounced = 'bounced',
    Clicked = 'clicked',
    Completed = 'completed',
    CompletionRate = 'completionRate',
    Ctr = 'ctr',
    Delivered = 'delivered',
    Detractors = 'detractors',
    Nps = 'nps',
    Opened = 'opened',
    Passives = 'passives',
    Promoters = 'promoters',
    Rating = 'rating',
    RatingBuckets = 'ratingBuckets',
    ResponseRate = 'responseRate',
    Sent = 'sent',
    Started = 'started',
    Syndicated = 'syndicated',
    TopBox = 'topBox', // uses standard widget overall selection (could technically use top 2)
    TopOneBox = 'topOneBox', // explicitly top 1
    TopTwoBox = 'topTwoBox',
    TopThreeBox = 'topThreeBox',
}

export enum KeyDriverStatus {
    Started = 0,
    Failed = 1,
    Succeeded = 1,
}

export interface Category {
    id: string;
    label: string;
    sentiment: string;
}

export interface SaveSurveyResultActions {
    accumulateCompletionTime?: boolean;
    alert?: boolean;
    checkSuspects?: boolean;
    fixPIIFields?: boolean;
    updateFlagsAndLabels?: boolean;
    forceNewSurveyChangeEvent?: boolean;
}

export interface SurveyTakerInfo {
    email?: string | null;
    phone?: string | null;
}

export enum RecordType {
    Survey = 0,
    Review = 1,
    Request = 2,
}

export interface SurveyTakerInfoLookUp {
    id: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    surveyTemplateID?: string | null;
    type: RecordType;
    recordJson: string;
}

export enum ModuleType {
    GSR = 1,
}

export interface Theme {
    [key: string]: any; // TODO
}

export interface RangeCondition {
    max: number;
    min: number;
}

export interface JumpTarget {
    gotoQuestionID: QuestionID;
    percentage?: number;
}

export interface JumpLogic {
    jumpTargets: JumpTarget[];
    rangeCondition?: RangeCondition | null;
    rowID: QuestionID;
    optionID: OptionID | null;
}

export interface SurveyResponsesAuth {
    View: boolean;
    Edit: boolean;
    SMSResponding: boolean;
}

export const SURVEY1_TEMPLATE_ID = '5e373c45d3f65fcab554e7b0';
export const SURVEY1_TEMPLATE_NAME = 'Simple Surveys';
export const KIOSK_TEMPLATE_ID = '6960f4c98781d0aae9ac8d72';
export const KIOSK_TEMPLATE_NAME = 'Kiosk Surveys';
export const SYSTEM_GSR_SURVEY_TEMPLATE_ID = '5f313aed3f0f9c000681ea3b';
export const SYSTEM_GSR_REQUEST_TEMPLATE_ID = '5ef3c2c01c5f8275052e07a2';
export const SURVEY_DEFAULT_TYPE = 'Survey';
export const RATING_DEFAULT_MIN = 0;
export const RATING_DEFAULT_MAX = 5;

export const YES = 'yes';
export const NO = 'no';

// keys for customizationParameters used in customizeSyndicationPlans
export const SYNDICATION_TEMPLATE_ID = 'stID';
export const SYNDICATION_RESULT_ID = 'srID';

export interface Option {
    label: string;
    value: string;
}

export interface Pathing {
    enabled?: boolean;
    rowOptions?: Option[];
    optionOptions?: { [rowValue: string]: Option[] };
    rows?: Option[];
    options?: Option[];
    pageOptions: Option[];
    questionOptions: { [key: string]: Option[] };
    pageJumpStatus: string;
}

export enum ReviewElementTypes {
    Comment = 3,
    Rating = 6,
    Date = 11,
    Email = 13,
}
